
import { useEffect, useState } from 'react';
import { RSVPProvider, useRSVP } from '../context/RSVPContext';
import { useEventData } from '../hooks/useEventData';
import { useGuestData } from '../hooks/useGuestData';
import { RSVPNavigation } from './RSVPNavigation';
import { RSVPStepContainer } from './RSVPStepContainer';
import { RSVPHeader } from './RSVPHeader';
import { RSVPErrorBoundary } from './RSVPErrorBoundary';
import { RSVPWelcomeModal } from './RSVPWelcomeModal';
import { supabase } from '@/integrations/supabase/client';

// Main Flow Content (Used inside Provider)
const RSVPFlowContent = () => {
  const { 
    currentState, 
    eventId,
    setEventData,
    setAvailableGuests,
    setRespondedGuests,
    setInProgressGuests,
    error,
    isLoading,
  } = useRSVP();

  const [showWelcomeModal, setShowWelcomeModal] = useState(true);
  const [hasRecommendations, setHasRecommendations] = useState(false);

  // Load event data
  const { 
    eventData, 
    isLoading: isLoadingEvent,
    error: eventError
  } = useEventData(eventId!);

  // Load guest data
  const {
    availableGuests,
    respondedGuests,
    inProgressGuests,
    isLoading: isLoadingGuests,
    error: guestsError
  } = useGuestData(eventId!);

  // Check if all guests have RSVP'd
  const allGuestsResponded = respondedGuests.length > 0 && 
    availableGuests.length === 0 && inProgressGuests.length === 0;

  // Fetch recommendations data
  useEffect(() => {
    if (eventId) {
      const checkForRecommendations = async () => {
        // Check for venue recommendations
        const { data: venueRecommendations } = await supabase
          .from('event_venue_recommendations')
          .select('recommendation_id')
          .eq('event_id', eventId);
          
        // Check for date recommendations
        const { data: dateRecommendations } = await supabase
          .from('event_date_recommendations')
          .select('recommendation_id')
          .eq('event_id', eventId);
          
        setHasRecommendations(
          (venueRecommendations && venueRecommendations.length > 0) || 
          (dateRecommendations && dateRecommendations.length > 0)
        );
      };
      
      checkForRecommendations();
    }
  }, [eventId]);

  // Update context with fetched data
  useEffect(() => {
    if (eventData) {
      setEventData(eventData);
    }
  }, [eventData, setEventData]);

  useEffect(() => {
    if (availableGuests.length > 0) {
      setAvailableGuests(availableGuests);
    }
    if (respondedGuests.length > 0) {
      setRespondedGuests(respondedGuests);
    }
    if (inProgressGuests.length > 0) {
      setInProgressGuests(inProgressGuests);
    }
  }, [availableGuests, respondedGuests, inProgressGuests, setAvailableGuests, setRespondedGuests, setInProgressGuests]);

  const closeWelcomeModal = () => {
    setShowWelcomeModal(false);
  };

  // Show loading state if any data is loading
  if (isLoading || isLoadingEvent || isLoadingGuests) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // Show error state if any error occurred
  if (error || eventError || guestsError) {
    return (
      <div className="text-center p-6 text-red-500">
        <h3 className="text-lg font-semibold mb-2">Error</h3>
        <p>{(error || eventError || guestsError)?.message || 'An error occurred'}</p>
      </div>
    );
  }

  // Get the host's first name
  const hostName = eventData?.hostName 
    ? eventData.hostName.split(' ')[0] 
    : 'Your host';

  return (
    <div className="flex flex-col min-h-screen">
      {/* Welcome Modal */}
      <RSVPWelcomeModal 
        isOpen={showWelcomeModal}
        onClose={closeWelcomeModal}
        hostName={hostName}
        eventName={eventData?.name || 'Event'}
        description={eventData?.description}
        deadline={eventData?.event_deadline}
        guestRsvpComplete={allGuestsResponded}
        hasRecommendations={hasRecommendations}
      />
      
      <RSVPHeader 
        eventName={eventData?.name || 'Event'} 
        hostName={eventData?.hostName || 'Host'} 
        currentState={currentState} 
      />
      
      <div className="flex-grow">
        <RSVPStepContainer 
          currentState={currentState} 
          eventData={eventData!}
          availableGuests={availableGuests}
          respondedGuests={respondedGuests}
          inProgressGuests={inProgressGuests}
        />
      </div>
      
      <RSVPNavigation 
        currentState={currentState} 
      />
    </div>
  );
};

// Wrapper component with Provider
interface RSVPFlowProps {
  eventId: string;
}

export const RSVPFlow = ({ eventId }: RSVPFlowProps) => {
  return (
    <RSVPErrorBoundary>
      <RSVPProvider eventId={eventId}>
        <RSVPFlowContent />
      </RSVPProvider>
    </RSVPErrorBoundary>
  );
};
